import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useFeatureFlag(featureKey: string) {
  const { data, isLoading } = useQuery({
    queryKey: ["feature-flag", featureKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_feature_flags")
        .select("is_enabled, updated_at")
        .eq("feature_key", featureKey)
        .maybeSingle();
      if (error) return { is_enabled: false, updated_at: null as string | null };
      return {
        is_enabled: !!data?.is_enabled,
        updated_at: (data?.updated_at as string | null) ?? null,
      };
    },
    staleTime: 60_000,
  });

  return {
    isEnabled: !!data?.is_enabled,
    updatedAt: data?.updated_at ?? null,
    isLoading,
  };
}

export function useToggleFeatureFlag(featureKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (nextEnabled: boolean) => {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("platform_feature_flags")
        .update({
          is_enabled: nextEnabled,
          updated_at: new Date().toISOString(),
          updated_by: auth.user?.id ?? null,
        })
        .eq("feature_key", featureKey);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flag", featureKey] });
    },
  });
}
